import React, { useEffect, useRef, useState } from 'react';

export interface ColumnDef<T> {
  key: string;
  label: string;
  render?: (row: T) => React.ReactNode;
  defaultVisible?: boolean;
}

interface ColumnsPickerProps<T> {
  columns: ColumnDef<T>[];
  storageKey: string;
  /** When provided with toggleColumn, picker state is controlled by the parent (shared with table). */
  visibleKeys?: string[];
  toggleColumn?: (key: string) => void;
}

export function useVisibleColumns<T>(columns: ColumnDef<T>[], storageKey: string) {
  const defaultKeys = columns.filter(c => c.defaultVisible !== false).map(c => c.key);
  const [visibleKeys, setVisibleKeys] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as string[];
        const valid = parsed.filter(key => columns.some(c => c.key === key));
        if (valid.length) {
          return valid;
        }
      }
    } catch {
      /* ignore */
    }
    return defaultKeys;
  });

  const visibleColumns = columns.filter(c => visibleKeys.includes(c.key));

  const toggleColumn = (key: string) => {
    setVisibleKeys(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      localStorage.setItem(storageKey, JSON.stringify(next));
      return next.length ? next : defaultKeys;
    });
  };

  return { visibleColumns, visibleKeys, toggleColumn };
}

export default function ColumnsPicker<T>({
  columns,
  storageKey,
  visibleKeys: controlledVisibleKeys,
  toggleColumn: controlledToggleColumn,
}: Readonly<ColumnsPickerProps<T>>) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const internal = useVisibleColumns(columns, storageKey);
  const visibleKeys = controlledVisibleKeys ?? internal.visibleKeys;
  const toggleColumn = controlledToggleColumn ?? internal.toggleColumn;

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handleClick = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div
      className="analytics-columns-wrap"
      ref={wrapRef}
    >
      <button
        type="button"
        className="analytics-columns-btn"
        onClick={() => setOpen(v => !v)}
      >
        Columns ▾
      </button>
      {open ? (
        <div className="analytics-columns-menu">
          {columns.map(col => (
            <label
              key={col.key}
              className="analytics-columns-option"
            >
              <input
                type="checkbox"
                checked={visibleKeys.includes(col.key)}
                onChange={() => toggleColumn(col.key)}
              />
              {col.label}
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}
